import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@dextech-ai/dex-design-system';
import IfConditionRow from './IfConditionRow';

function newCondition() {
	return { field: '', operator: 'const', value: '', required: true };
}

/**
 * Wraps the list of if-conditions + the negation (NOT) toggle + "Add condition"
 * button. Pure — owns no state; all edits propagate through `onChange`.
 */
export default function IfClauseEditor({
	ifConditions,
	negated,
	availableFields,
	ruleIndex,
	errors,
	onChange,
	onNegatedChange,
}) {
	const handleConditionChange = (idx, next) => {
		const copy = ifConditions.slice();
		copy[idx] = next;
		onChange(copy);
	};

	const handleConditionRemove = (idx) => {
		onChange(ifConditions.filter((_, i) => i !== idx));
	};

	const handleAdd = () => {
		onChange([...ifConditions, newCondition()]);
	};

	const conditionCount = ifConditions.length;
	const meta = negated
		? 'match NONE of these conditions'
		: (conditionCount > 1 ? 'match ALL of these conditions (AND)' : 'match this condition');

	return (
		<section
			className="dsv-xallof-builder__section dsv-xallof-builder__section--if"
			aria-labelledby={`xallof-rule-${ruleIndex}-if-heading`}
		>
			<header className="dsv-xallof-builder__section-head">
				<span
					id={`xallof-rule-${ruleIndex}-if-heading`}
					className="dsv-xallof-builder__section-label dsv-xallof-builder__section-label--if"
				>
					IF
				</span>
				<Checkbox
					className="dsv-xallof-builder__not-toggle"
					checked={!!negated}
					onCheckedChange={onNegatedChange}
					data-testid={`xallof-rule-${ruleIndex}-not-toggle`}
				>
					NOT
				</Checkbox>
				<span className="dsv-xallof-builder__section-meta">{meta}</span>
			</header>

			{conditionCount === 0 && (
				<p className="dsv-xallof-builder__hint">
					Add a condition to define when this rule applies.
				</p>
			)}

			<div className="dsv-xallof-builder__cond-list">
				{ifConditions.map((cond, idx) => (
					<IfConditionRow
						key={idx}
						condition={cond}
						condIndex={idx}
						ruleIndex={ruleIndex}
						availableFields={availableFields}
						errors={errors?.[String(idx)]}
						onChange={(next) => handleConditionChange(idx, next)}
						onRemove={() => handleConditionRemove(idx)}
					/>
				))}
			</div>

			<button
				type="button"
				className="dsv-xallof-builder__add-cond"
				onClick={handleAdd}
				data-testid={`xallof-rule-${ruleIndex}-add-if-cond`}
			>
				+ Add condition
			</button>
		</section>
	);
}

IfClauseEditor.propTypes = {
	ifConditions: PropTypes.array.isRequired,
	negated: PropTypes.bool,
	availableFields: PropTypes.array.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	onNegatedChange: PropTypes.func.isRequired,
};
